import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { env } from '../config/env';
import { useAuthStore } from '../stores/auth';
import type {
  DebugTraceEvent,
  ReadTheRoomTraceEntry,
  ReadTheRoomJudgeUpdate,
  InferenceDetailEntry,
} from '../services/api/coachActiveSession';

/** MED-1105 — cap the unified debug stream so a long session can't grow it unbounded. */
const MAX_DEBUG_EVENTS = 500;

/** Shape of the SI `inference` socket event (the AI's per-window classification). */
interface InferenceEvent {
  timestamp: string;
  status: 'debounced' | 'called' | 'result' | 'error';
  latencyMs?: number;
  result?: {
    type: 'nudge' | 'none' | 'error';
    subtype?: string;
    message?: string;
  };
  windowLines?: number;
  error?: string;
  /** Recipe path: the AI's decision this tick, even when no nudge fires. */
  decision?: 'nudge' | 'none';
  nudgeType?: string;
}

/**
 * MED-1105 — render ONE completed analysis (`inference` result/error) as a
 * numbered debug row with the response in detail. Debounced ticks are NOT
 * rendered as rows — they're aggregated into a counter (see the hook) to keep
 * the timeline concise for QA.
 */
function analysisToDebug(d: InferenceEvent, n: number): DebugTraceEvent {
  const lines = d.windowLines ?? '?';
  const ms = d.latencyMs != null ? ` · ${d.latencyMs}ms` : '';
  let status: DebugTraceEvent['status'] = 'ok';
  let detail: string;
  if (d.status === 'error') {
    status = 'error';
    detail = `Analysis #${n} — error: ${d.error ?? 'unknown'}`;
  } else if (d.result?.type === 'nudge') {
    detail = `Analysis #${n} — DRIFT → ${d.result.subtype ?? 'topic_alignment'} nudge (${lines} lines${ms}): ${d.result.message ?? ''}`;
  } else if (d.result?.type === 'none') {
    detail = `Analysis #${n} — on-topic, no nudge (${lines} lines${ms})`;
  } else if (d.decision === 'nudge') {
    detail = `Analysis #${n} — DECISION: nudge${d.nudgeType ? ` (${d.nudgeType})` : ''} → see nudge below (${lines} lines${ms})`;
  } else if (d.decision === 'none') {
    detail = `Analysis #${n} — DECISION: no nudge / on-topic (${lines} lines${ms})`;
  } else {
    detail = `Analysis #${n} — analyzed ${lines} lines${ms}`;
  }
  return {
    ts: new Date().toISOString(),
    source: 'session-intelligence',
    hop: 'inference',
    status,
    detail,
    data: { windowLines: d.windowLines, latencyMs: d.latencyMs },
  };
}

/**
 * MED-872 / 2.3b — shared in-session AI nudge stream.
 *
 * Lifts the Socket.IO `nudge` subscription out of the SessionLivePage debug
 * page so the real Coach Active Session screen can consume the same stream for
 * both the AI Insights panel (tabbed history) and the bottom-left toasts.
 *
 * The wire contract is owned by medley-services/session-intelligence
 * (`src/types/index.ts`). Recipe v5 — live nudges carry the dimension the AI
 * actually observed (rebalance_speaking, reflect_silence, check_understanding,
 * missed_coaching_opportunity) instead of everything flattening to
 * 'topic_alignment'; `nudgeCategory` buckets all of them as 'live'.
 * `'pre_session'` is reserved for the Story 1.5 pre-session synthesis path
 * (the AI Insights "Pre-work" tab stays hidden until those nudges actually
 * arrive — AC5).
 */
export interface SessionNudge {
  id: string;
  type: 'nudge';
  subtype:
    | 'topic_alignment'
    | 'pacing'
    | 'pre_session'
    | 'rebalance_speaking'
    | 'reflect_silence'
    | 'check_understanding'
    | 'missed_coaching_opportunity'
    /** MED-1283 — a read-the-room the SERVER escalated to on its own after the
     *  automatic nudge recipe went quiet, not a leader tapping Generate
     *  Recommendation. Falls through nudgeCategory's default to 'live'; listed
     *  here so the union matches the wire and the LIVE tag can name it. */
    | 'ambient_read';
  message: string;
  /** Optional suggested prompt the coach can copy / save (LIVE nudges). */
  source?: string;
  timestamp: string; // ISO 8601
  dismissed: boolean;
  /**
   * MED-1167 — in-session "Pin" triage flag. A pinned nudge leaves the New inbox
   * (and its category view) and moves to the Pinned tab, keeping the whole card.
   * In-session only, like `dismissed` — never persisted; arrives falsy off the
   * wire and is set true only by the leader's Pin action.
   */
  pinned: boolean;
}

/**
 * MED-1166 — ack payload for the on-demand `generate_recommendation` request.
 * Wire contract owned by session-intelligence (`src/types/index.ts`). Three
 * visible outcomes (AC4): a recommendation, a calm no-read (with the reason so
 * the Guide panel can word the empty state honestly), or a transient error.
 */
export type ReadTheRoomAck =
  | {
      status: 'ok';
      /** v3 — anchor_quote is the verbatim window fragment the read pulls on
       *  (omitted when SI's containment guard stripped it). */
      recommendation: {
        noticing: string;
        suggested_prompt: string;
        anchor_quote?: string;
      };
    }
  | {
      status: 'no_read';
      /** 'nothing_new' (v3) — honest exhaustion: every live thread already
       *  pulled. Unknown/future reasons fall back to the generic empty copy. */
      reason:
        | 'paused'
        | 'insufficient_transcript'
        | 'no_confident_read'
        | 'nothing_new';
    }
  | { status: 'error' };

/** UI category derived from the wire subtype — drives tabs, color, dismiss behavior. */
export type NudgeCategory = 'live' | 'pace' | 'pre_work';

export function nudgeCategory(nudge: SessionNudge): NudgeCategory {
  switch (nudge.subtype) {
    case 'pacing':
      return 'pace';
    case 'pre_session':
      return 'pre_work';
    case 'topic_alignment':
    default:
      return 'live';
  }
}

/** Pacing nudges persist until dismissed; all other categories auto-dismiss as toasts (AC5). */
export function nudgePersistsAsToast(nudge: SessionNudge): boolean {
  return nudgeCategory(nudge) === 'pace';
}

export interface UseSessionNudgesResult {
  /** Newest-first. Includes dismissed nudges so the panel keeps full history. */
  nudges: SessionNudge[];
  /** Nudges arrived in the last few seconds — drives the panel's "just arrived" emphasis. */
  highlightedNudgeIds: Set<string>;
  /** True while the Socket.IO transport is connected to session-intelligence. */
  isConnected: boolean;
  /** Marks a nudge dismissed (kept in history, hidden from active surfaces). */
  dismissNudge: (id: string) => void;
  /** MED-1167 — marks a nudge pinned (moves it from New to the Pinned tab; in-session only). */
  pinNudge: (id: string) => void;
  /** DEV-only: inject a local nudge for QA without a live Bedrock round-trip. */
  injectDemoNudge: (category: NudgeCategory) => void;
  /**
   * MED-1223 — inject a locally-computed nudge into the same stream SI nudges
   * use, so it renders through the identical toast/panel path with working
   * highlight, pin, and dismiss. De-duped by `id` like every nudge (a repeat id
   * is a no-op), which backs the agenda-pacing one-per-section cadence. Unlike
   * `injectDemoNudge` this is NOT dev-gated — pacing is a shipping feature.
   */
  addLocalNudge: (nudge: SessionNudge) => void;
  /** MED-953 — true while the coach has AI listening paused for this session. */
  aiPaused: boolean;
  /** MED-953 — emit pause_ai / resume_ai (coach-only; server role-guards too). */
  setAiListeningPaused: (paused: boolean) => void;
  /**
   * MED-1166 — request an on-demand "read the room" over the live socket.
   * Resolves to the ack and NEVER throws: disconnected socket, timeout, or a
   * transport failure all resolve to `{ status: 'error' }` so the button's
   * spinner always settles into a visible outcome (AC4).
   */
  generateRecommendation: () => Promise<ReadTheRoomAck>;
  /**
   * MED-798 — ms epoch of the most recent transcript line received from
   * session-intelligence, or null if none yet this connection. Lets the panel
   * distinguish "connected but the bot isn't feeding audio" (e.g. a Recall bot
   * that joined but was never admitted to the meeting) from "actively hearing
   * the room" — the socket being connected alone never proved audio was flowing.
   */
  lastTranscriptAt: number | null;
  /**
   * MED-1169 — ms epoch of the AI's most recent COMPLETED analysis of the
   * conversation (an `inference` event with status 'result'; never 'error' or
   * 'debounced' — a failed or skipped tick is not a check). Null before the
   * first analysis. A SESSION fact, not a connection fact: it survives socket
   * reconnects/token refreshes and resets only when the session changes —
   * during an outage it honestly ages, which is the "analysis loop is down"
   * tell the liveness heartbeat exists to expose.
   */
  lastCheckAt: number | null;
  /**
   * MED-1169 — the decision of that last completed analysis. Drives the
   * heartbeat's "nothing to flag" suffix, which must NEVER be false: it shows
   * only when the last decision was literally 'none' (unknown → omitted).
   */
  lastCheckDecision: 'nudge' | 'none' | null;
  /**
   * MED-1276 — ms epoch from which a completed analysis is OVERDUE: the first
   * transcript line of the current listening run. Cleared on pause (nothing is
   * analyzed while paused, so that gap is expected) and re-stamped by the first
   * line after resume. Lets the panel call out a stalled analysis loop even
   * before the first check ever lands — the state the 2026-07-29 session sat in
   * for 20 minutes, where `lastCheckAt` stayed null and the panel therefore had
   * nothing at all to show under a green "Listening active".
   */
  checksDueSince: number | null;
  /**
   * MED-1105 — unified in-session debug stream for the dev debug drawer:
   * bot-join hops (`bot_trace_event`) + live transcript lines + the AI's
   * `inference` classifications + `nudge`s, in arrival order. Consumed only by
   * the drawer; empty for everyone else. Capped to the most recent N.
   */
  debugEvents: DebugTraceEvent[];
  /**
   * MED-1210 — read-the-room diagnostic traces (why 'Generate Recommendation'
   * did/didn't surface a prompt), newest last. Only ever populated for the
   * leader who clicked (SI emits point-to-point); empty for everyone else.
   */
  readTheRoomTraces: ReadTheRoomTraceEntry[];
  /**
   * MED-1245 (Tier 1) — per-window rationale for the AUTOMATIC nudge path (the
   * model's `why` + confidence + why a would-be nudge did/didn't surface),
   * newest last. Leader-scoped on the wire (SI emits to the leaders room only);
   * empty for a non-leader viewer.
   */
  inferenceDetails: InferenceDetailEntry[];
  /**
   * MED-1105 — aggregate inference counters for the debug pane: how many
   * transcript blocks were actually ANALYZED by the AI vs DEBOUNCED (skipped
   * because not enough new content). Keeps the timeline concise — debounced
   * ticks are a counter, not individual rows.
   */
  inferenceStats: { analyses: number; debounced: number };
}

interface UseSessionNudgesOptions {
  sessionId: string | undefined;
  /** Only connect + subscribe when the AI gate is open (AC3/AC6). */
  enabled: boolean;
  /**
   * MED-1280 — forward-only admission gate. Called once as each nudge ARRIVES
   * (from the SI socket or `addLocalNudge`); return `false` to reject it so it
   * never enters the stream. This is a decision made a single time, at arrival —
   * unlike a render-time `filter`, which re-evaluates on every render and can
   * retroactively remove a nudge the leader was already shown (the MED-1280
   * defect, Mechanism B). A nudge that passes the gate is permanent until the
   * leader's own Dismiss (AC2/AC6). Omitted → admit everything.
   */
  shouldAdmitNudge?: (nudge: SessionNudge) => boolean;
}

const HIGHLIGHT_MS = 6000;

/**
 * MED-1276 — a transcript gap at least this long ENDS the current listening
 * run. Past it the panel has already stopped counting us as hearing the room
 * (its TRANSCRIPT_FRESH_MS), so no analysis was owed across the gap and the
 * due-since clock has to restart with the audio — otherwise a room that went
 * quiet for five minutes comes back reading "Analysis stalled" for the ~30s
 * until the next check lands, which is a false alarm in the one place the panel
 * cannot afford one. Deliberately EQUAL to the panel's window: shorter would
 * restart the clock while the header still says "Listening active" (hiding a
 * real stall), longer would report a resumed conversation as a stall.
 */
const LISTENING_RUN_GAP_MS = 30_000;

/** MED-1166 — bound on the on-demand generation round-trip (model call included). */
const GENERATE_RECOMMENDATION_TIMEOUT_MS = 30_000;

export function useSessionNudges({
  sessionId,
  enabled,
  shouldAdmitNudge,
}: UseSessionNudgesOptions): UseSessionNudgesResult {
  const { accessToken } = useAuthStore();
  // MED-1280 — hold the admission gate in a ref so `addNudge` (a dependency of
  // the socket effect) keeps a stable identity while always reading the latest
  // predicate; otherwise a changing gate would tear down and re-open the socket.
  const shouldAdmitNudgeRef = useRef(shouldAdmitNudge);
  shouldAdmitNudgeRef.current = shouldAdmitNudge;
  const [nudges, setNudges] = useState<SessionNudge[]>([]);
  const [highlightedNudgeIds, setHighlightedNudgeIds] = useState<Set<string>>(
    new Set()
  );
  const [isConnected, setIsConnected] = useState(false);
  const [aiPaused, setAiPaused] = useState(false);
  const [lastTranscriptAt, setLastTranscriptAt] = useState<number | null>(null);
  // MED-1169 — heartbeat of the last completed analysis. Deliberately NOT
  // cleared in the socket effect's cleanup (which also runs on token-refresh
  // reconnects mid-session): "the AI last checked at T" is a fact about the
  // session, so it resets only when the session changes (effect below).
  const [lastCheck, setLastCheck] = useState<{
    at: number;
    decision: 'nudge' | 'none' | null;
  } | null>(null);
  // MED-1276 — when analyses became due (first transcript line of the current
  // listening run). A session fact like lastCheck, for the same reason: an
  // outage or a token-refresh reconnect must NOT restart the staleness clock,
  // or the stall hides itself.
  const [checksDueSince, setChecksDueSince] = useState<number | null>(null);
  const [debugEvents, setDebugEvents] = useState<DebugTraceEvent[]>([]);
  // MED-1210 — read-the-room diagnostic traces (capped like debugEvents).
  const [readTheRoomTraces, setReadTheRoomTraces] = useState<
    ReadTheRoomTraceEntry[]
  >([]);
  // MED-1245 — automatic-nudge rationale details (capped like debugEvents).
  const [inferenceDetails, setInferenceDetails] = useState<
    InferenceDetailEntry[]
  >([]);
  const [inferenceStats, setInferenceStats] = useState<{
    analyses: number;
    debounced: number;
  }>({
    analyses: 0,
    debounced: 0,
  });
  const analysisSeqRef = useRef(0);
  // MED-1276 — arrival time of the previous transcript line, read synchronously
  // inside the socket handler to detect a break in the listening run (the state
  // above is a render value and lags). NOT cleared on socket teardown: a
  // token-refresh reconnect must not look like a gap, for the same reason
  // lastCheck survives it.
  const lastTranscriptAtRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const highlightTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const highlight = useCallback((id: string) => {
    setHighlightedNudgeIds(prev => new Set(prev).add(id));
    const existing = highlightTimers.current.get(id);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      setHighlightedNudgeIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      highlightTimers.current.delete(id);
    }, HIGHLIGHT_MS);
    highlightTimers.current.set(id, timer);
  }, []);

  const addNudge = useCallback(
    (nudge: SessionNudge) => {
      // MED-1280 — forward-only admission gate: a rejected nudge never enters
      // the stream (and never highlights). Runs once, here at arrival, so the
      // decision can't reach back and remove a nudge already surfaced.
      if (shouldAdmitNudgeRef.current && !shouldAdmitNudgeRef.current(nudge)) {
        return;
      }
      setNudges(prev =>
        prev.some(n => n.id === nudge.id) ? prev : [nudge, ...prev]
      );
      highlight(nudge.id);
    },
    [highlight]
  );

  const dismissNudge = useCallback((id: string) => {
    setNudges(prev =>
      prev.map(n => (n.id === id ? { ...n, dismissed: true } : n))
    );
  }, []);

  // MED-1167 — Pin mirrors dismiss: an in-memory flag flip, no persistence.
  const pinNudge = useCallback((id: string) => {
    setNudges(prev =>
      prev.map(n => (n.id === id ? { ...n, pinned: true } : n))
    );
  }, []);

  const injectDemoNudge = useCallback(
    (category: NudgeCategory) => {
      if (!import.meta.env.DEV) return;
      const subtype: SessionNudge['subtype'] =
        category === 'pace'
          ? 'pacing'
          : category === 'pre_work'
            ? 'pre_session'
            : 'topic_alignment';
      const demo: SessionNudge = {
        id: crypto.randomUUID(),
        type: 'nudge',
        subtype,
        message:
          category === 'pace'
            ? 'Opening Discussion is 2:14 over planned 10 min. Consider transitioning to Framework Exploration.'
            : category === 'pre_work'
              ? 'Emma flagged elevated stress vs Session 2. Create space for her early.'
              : 'Mehak used "stuck" twice — consider a direct invite when the floor opens.',
        source:
          category === 'live'
            ? "Mehak, I noticed you used the word 'stuck' — would you be open to sharing more about what that feels like? Sometimes naming it helps."
            : undefined,
        timestamp: new Date().toISOString(),
        dismissed: false,
        pinned: false,
      };
      addNudge(demo);
    },
    [addNudge]
  );

  // MED-1223 — public, non-dev entry point for locally-computed nudges (agenda
  // pacing). Thin wrapper over addNudge so pacing nudges share the exact same
  // array, highlight, and id-dedupe as SI nudges.
  const addLocalNudge = useCallback(
    (nudge: SessionNudge) => addNudge(nudge),
    [addNudge]
  );

  // Socket.IO subscription — only while the AI gate is open and a session is set.
  useEffect(() => {
    if (!enabled || !sessionId) {
      setIsConnected(false);
      return;
    }

    const socket = io(env.sessionIntelligenceUrl, {
      query: { sessionId },
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
    });

    // MED-1105 — append any in-session event to the unified debug stream the
    // dev debug drawer renders (bot-join hops + transcript + inference + nudges).
    const pushDebug = (event: DebugTraceEvent) => {
      setDebugEvents(prev => {
        const next = [...prev, event];
        return next.length > MAX_DEBUG_EVENTS
          ? next.slice(next.length - MAX_DEBUG_EVENTS)
          : next;
      });
    };

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));
    socket.on('connect_error', () => setIsConnected(false));
    socket.on('nudge', (nudge: SessionNudge) => {
      addNudge(nudge);
      // MED-1169 — any arriving nudge (including timer-fired PACE nudges,
      // which come with no `inference` event) invalidates the heartbeat's
      // "nothing to flag" suffix until the next analysis decides 'none'.
      // The timestamp stays: the last completed check is still a fact.
      setLastCheck(prev =>
        prev && prev.decision === 'none' ? { ...prev, decision: 'nudge' } : prev
      );
      pushDebug({
        ts: new Date().toISOString(),
        source: 'session-intelligence',
        hop: `nudge:${nudge.subtype}`,
        status: 'ok',
        detail: nudge.message,
        data: nudge.source ? { suggestedPrompt: nudge.source } : undefined,
      });
    });
    // MED-953 — pause state broadcast (also emitted to us on connect).
    socket.on('ai_listening_state', (data: { paused: boolean }) => {
      setAiPaused(data.paused);
      // MED-1276 — SI processes no transcript while paused, so no analysis is
      // owed. Drop the due-since stamp; the first line after resume re-stamps
      // it, giving the loop a fresh grace period instead of reporting the
      // pause itself as a stall.
      if (data.paused) setChecksDueSince(null);
    });
    // MED-798 — SI emits a `transcript` per line; partial (interim) AND final.
    // Any arrival proves audio is flowing (stamp it), but to keep the debug pane
    // readable we only render COMPLETED sentences (isFinal), not the noisy partials.
    socket.on(
      'transcript',
      (data: {
        speaker: string;
        text: string;
        timestamp: string;
        isFinal: boolean;
        /**
         * MED-1277 — true only for lines pushed in through the debug drawer's
         * transcript injection; undefined for real speech. Mirrors
         * `ServerToClientEvents.transcript` in session-intelligence by hand —
         * nothing links the two type declarations, so they must be kept in step.
         */
        injected?: boolean;
      }) => {
        const at = Date.now();
        setLastTranscriptAt(at);
        // MED-1276 — first line of this listening run starts the clock the
        // panel measures a stalled analysis loop against. A long enough gap
        // since the previous line means the run ended and a new one starts
        // here: nothing was owed while the room was silent, so the clock
        // restarts rather than counting that silence as a stall.
        const runBroke =
          lastTranscriptAtRef.current == null ||
          at - lastTranscriptAtRef.current >= LISTENING_RUN_GAP_MS;
        lastTranscriptAtRef.current = at;
        setChecksDueSince(prev => (prev != null && !runBroke ? prev : at));
        if (!data.isFinal) return;
        pushDebug({
          ts: new Date().toISOString(),
          source: 'recall',
          hop: 'transcript',
          status: 'ok',
          detail: `${data.speaker}: ${data.text}`,
          // MED-1277 — keep `hop: 'transcript'` so the drawer's Transcript
          // filter chip and line counter (both string-match the hop) still see
          // it; the injected/real distinction rides in `data` and is rendered
          // as a badge instead.
          data: data.injected ? { injected: true } : undefined,
        });
      }
    );
    // MED-1105 — the AI's per-window classification. Debounced ticks (not enough
    // new content to analyze) are aggregated into a counter, NOT individual rows;
    // each REAL analysis becomes one numbered row with the decision in detail.
    socket.on('inference', (data: InferenceEvent) => {
      if (data.status === 'debounced') {
        setInferenceStats(s => ({ ...s, debounced: s.debounced + 1 }));
        return;
      }
      // MED-1169 — stamp the liveness heartbeat on COMPLETED analyses only
      // (never 'error'/'called' — a failed or in-flight call is not a check).
      // Both wire shapes are live: the recipe path carries `decision`, the
      // legacy fallback path carries `result.type` (mirrors analysisToDebug).
      if (data.status === 'result') {
        const decision =
          data.decision ??
          (data.result?.type === 'nudge'
            ? ('nudge' as const)
            : data.result?.type === 'none'
              ? ('none' as const)
              : null);
        setLastCheck({ at: Date.now(), decision });
      }
      analysisSeqRef.current += 1;
      setInferenceStats(s => ({ ...s, analyses: analysisSeqRef.current }));
      pushDebug(analysisToDebug(data, analysisSeqRef.current));
    });
    // MED-1105 — bot-join trace hops (already DebugTraceEvent-shaped).
    socket.on('bot_trace_event', (event: DebugTraceEvent) => pushDebug(event));
    // MED-1210 — read-the-room diagnostic traces (delivered to THIS leader's
    // socket only). Appended newest-last; capped to the same bound.
    socket.on('read_the_room_trace', (trace: ReadTheRoomTraceEntry) => {
      setReadTheRoomTraces(prev => {
        const next = [...prev, trace];
        return next.length > MAX_DEBUG_EVENTS
          ? next.slice(next.length - MAX_DEBUG_EVENTS)
          : next;
      });
    });
    // MED-1210 — the sampled async judge verdict; merge it into its trace by traceId.
    socket.on('read_the_room_judge', (update: ReadTheRoomJudgeUpdate) => {
      setReadTheRoomTraces(prev =>
        prev.map(t =>
          t.traceId === update.traceId ? { ...t, judge: update.verdict } : t
        )
      );
    });
    // MED-1245 — automatic-nudge rationale (delivered to THIS leader's socket
    // only via the leaders sub-room). Appended newest-last; capped identically.
    socket.on('inference_detail', (detail: InferenceDetailEntry) => {
      setInferenceDetails(prev => {
        const next = [...prev, detail];
        return next.length > MAX_DEBUG_EVENTS
          ? next.slice(next.length - MAX_DEBUG_EVENTS)
          : next;
      });
    });

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      // MED-1223 — do NOT reset `aiPaused` here. This cleanup also runs on
      // token-refresh reconnects mid-session (see the lastCheck note above), and
      // resetting to `false` opened a window — between teardown and the server
      // re-broadcasting `ai_listening_state` on reconnect — where the paused
      // badge briefly reported "not paused". MED-1223's client-side pacing clock
      // keys AC7 off this flag, so that flicker let a pacing nudge fire while the
      // coach had AI paused. Pause is a server-authoritative session fact
      // (re-asserted on connect); clear it only when the session changes (below).
      setLastTranscriptAt(null);
      setDebugEvents([]);
      setInferenceStats({ analyses: 0, debounced: 0 });
      analysisSeqRef.current = 0;
    };
  }, [enabled, sessionId, accessToken, addNudge]);

  // MED-1169 / MED-1223 / MED-1276 — the heartbeat, its due-since clock, and the
  // AI-paused flag are session facts: reset them only when the session itself
  // changes (not on reconnects — see the state's comment). The server
  // re-broadcasts the true pause state on connect, so a new session starting
  // `false` is corrected the moment its socket says otherwise.
  useEffect(() => {
    setLastCheck(null);
    setChecksDueSince(null);
    setAiPaused(false);
  }, [sessionId]);

  // MED-953 — coach pause/resume control. State flips when the server
  // broadcasts ai_listening_state back (single source of truth).
  const setAiListeningPaused = useCallback((paused: boolean) => {
    socketRef.current?.emit(paused ? 'pause_ai' : 'resume_ai');
  }, []);

  // MED-1166 — on-demand "read the room". Request/response on the existing
  // socket via a Socket.IO ack (the result goes only to this client; it never
  // enters the nudge stream). Generation runs a live model call in
  // session-intelligence, so the timeout is generous — but bounded, so the
  // Generate Recommendation spinner can never hang (AC4).
  const generateRecommendation =
    useCallback(async (): Promise<ReadTheRoomAck> => {
      const socket = socketRef.current;
      if (!socket || !socket.connected) return { status: 'error' };
      try {
        return (await socket
          .timeout(GENERATE_RECOMMENDATION_TIMEOUT_MS)
          .emitWithAck('generate_recommendation')) as ReadTheRoomAck;
      } catch {
        // Timed out or the transport dropped mid-request — AC4 transient error.
        return { status: 'error' };
      }
    }, []);

  // Clean up highlight timers on unmount.
  useEffect(() => {
    const timers = highlightTimers.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  return {
    nudges,
    highlightedNudgeIds,
    isConnected,
    dismissNudge,
    pinNudge,
    injectDemoNudge,
    addLocalNudge,
    aiPaused,
    setAiListeningPaused,
    generateRecommendation,
    lastTranscriptAt,
    lastCheckAt: lastCheck?.at ?? null,
    lastCheckDecision: lastCheck?.decision ?? null,
    checksDueSince,
    debugEvents,
    readTheRoomTraces,
    inferenceDetails,
    inferenceStats,
  };
}
